import { inject, Injectable } from '@angular/core';
import { marked } from 'marked';
import { firstValueFrom, Observable } from 'rxjs';
import { BackendService } from '@ipg/ngx-frontend-toolset';
import {
  ArticleContent,
  Manifest,
  MenuItem,
  SearchResult,
} from '../models/docs.models';

/**
 * Owns all `/manuals/*` data access for the docs feature: manifest loading,
 * article fetching + caching, markdown rendering (including the screenshot
 * transform), media loading, and full-text search over cached content.
 *
 * Provided at the component level (see DocsComponent.providers) so the cache
 * lives for the lifetime of the docs view rather than the whole app.
 */
@Injectable()
export class DocsContentService {
  private readonly backend = inject(BackendService);
  private cache: Record<string, ArticleContent> = {};

  /** Fetch the navigation manifest. Returns undefined on empty response. */
  loadManifest(): Promise<Manifest | undefined> {
    return firstValueFrom(this.backend.get<Manifest>('/manuals/_index'));
  }

  /** Return a cached article without triggering a fetch, if present. */
  getCached(slug: string): ArticleContent | undefined {
    return this.cache[slug];
  }

  /**
   * Return the article for a slug, fetching and caching it on a miss. The
   * cached HTML is always fully processed (screenshot transform applied), so
   * cache hits and misses render identically. Returns null if not found.
   */
  async getArticle(slug: string): Promise<ArticleContent | null> {
    const cached = this.cache[slug];
    if (cached) return cached;

    const raw = await firstValueFrom(
      this.backend.get<string>(`/manuals/${slug}`, {
        responseType: 'text' as 'json',
      })
    );
    if (!raw) return null;

    const content: ArticleContent = { raw, html: await this.renderMarkdown(raw) };
    this.cache[slug] = content;
    return content;
  }

  /** Fetch a manual media asset (image) as a blob. */
  getMedia(src: string): Observable<Blob> {
    return this.backend.get<Blob>(`/manuals/media/${src}`, {
      responseType: 'blob' as 'json',
    });
  }

  /**
   * Preload and cache every manual so search can scan their content, then
   * return the matches for the given query as title/excerpt results.
   */
  async search(items: MenuItem[], query: string): Promise<SearchResult[]> {
    await this.preloadAll(items);

    const queryLower = query.toLowerCase();
    const results: SearchResult[] = [];

    for (const item of items) {
      const cached = this.cache[item.slug];
      if (!cached) continue;

      const rawLower = cached.raw.toLowerCase();
      const idx = rawLower.indexOf(queryLower);
      if (idx === -1) continue;

      // Extract a short excerpt around the first match.
      const start = Math.max(0, idx - 40);
      const end = Math.min(cached.raw.length, idx + query.length + 60);
      let excerpt = cached.raw.substring(start, end).replace(/\n/g, ' ').trim();

      if (start > 0) excerpt = '…' + excerpt;
      if (end < cached.raw.length) excerpt += '…';

      results.push({ slug: item.slug, title: item.title, excerpt });
    }

    return results;
  }

  async preloadAll(items: MenuItem[]): Promise<void> {
    const promises = items.map(async (item) => {
      if (this.cache[item.slug]) return;
      try {
        const raw = await firstValueFrom(
          this.backend.get<string>(`/manuals/${item.slug}`, {
            responseType: 'text' as 'json',
          })
        );
        if (raw) {
          this.cache[item.slug] = { raw, html: await this.renderMarkdown(raw) };
        }
      } catch {
        // Ignore preloading errors
      }
    });
    await Promise.allSettled(promises);
  }

  /**
   * Render raw manual markdown to HTML, applying the screenshot-comment
   * transform first so `<!-- screenshot: name -->` becomes an image.
   */
  private async renderMarkdown(raw: string): Promise<string> {
    const processedRaw = raw.replace(
      /<!--\s*screenshot:\s*([a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9]+)?)\s*-->/g,
      (_match, filename) => {
        const fullFilename = filename.includes('.') ? filename : `${filename}.png`;
        return `![Screenshot: ${filename}](${fullFilename})`;
      }
    );
    return (await marked.parse(processedRaw)) as string;
  }
}
